// Tier 2 practiceSchedule migration, Phase B: the real intended replacement for the groups portion
// of App.jsx's watchDoc mechanism — currently:
//   watchDoc("practiceSchedule", setPracticeSchedule, d=>({groups:d.groups||[], cancellations:...}))
// which subscribes to the single warriors/practiceSchedule document and reads its `groups` array
// field. This module instead subscribes to the new top-level `practiceGroups` collection (one
// document per group, doc ID = the group's own pg_* id — see scripts/migrate-practiceschedule.cjs)
// and reconstructs an equivalent in-memory array. Mirrors playersCollection.js/teamsCollection.js/
// coachesCollection.js exactly, one file per collection (practiceCancellationsCollection.js is the
// sibling for the other array).
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';

// Converts one practiceGroups/{id} document into the shape the app's in-memory `groups` array has
// always used. `nodeId` USED to be dropped here too ("nothing client-side reads it yet") — fixed
// proactively alongside players/teams/coaches, same reasoning: the shared node-filter component
// pattern means any collection could need nodeId next, and the silent-empty-result failure mode is
// costly enough to close now rather than case-by-case. Checked every write site that touches a
// practice-group-shaped object (the group editor's save handler and the "paste schedule" bulk
// importer, src/App.jsx) — both build their write payload from an explicitly-constructed `entry`/
// draft object (never spread from an existing group), or spread-then-immediately-override nodeId
// with the correct value in the same object literal, so carrying nodeId through here doesn't leak
// it into any write that shouldn't have it.
// `id` is already the doc ID (pg_* format, assigned client-side via `pg_${uid()}`), no conversion
// needed, same as teams/coaches and unlike players.
export function practiceGroupsDocToRecord(docId, data) {
  return { ...data, id: docId };
}

// Live-subscription replacement for the groups portion of watchDoc("practiceSchedule", ...) — same
// ergonomics (takes the setter, returns an unsubscribe function). Kept as the unscoped variant for
// coach/parent (neither has a node concept), matching practiceGroups' read rule staying
// isAllowed()||isParent() — admin now uses watchPracticeGroupsCollectionForNodes below.
export function watchPracticeGroupsCollection(setter) {
  return onSnapshot(collection(db, 'practiceGroups'), (snap) => {
    setter(snap.docs.map((d) => practiceGroupsDocToRecord(d.id, d.data())));
  });
}

// Node-scoped variant (mirroring watchTeamsCollectionForNodes/watchCoachesCollectionForNodes
// exactly) — constrained via where('nodeId','in',nodeIds) to a caller-supplied permitted-node list.
// Used for the admin role only; practiceGroups' READ rule itself stays unscoped for everyone this
// pass (see firestore.rules), so this is the admin management view narrowing itself for UX
// consistency, not a security boundary the rule enforces. Same 30-value 'in' ceiling as the others.
export function watchPracticeGroupsCollectionForNodes(nodeIds, setter) {
  return onSnapshot(query(collection(db, 'practiceGroups'), where('nodeId', 'in', nodeIds)), (snap) => {
    setter(snap.docs.map((d) => practiceGroupsDocToRecord(d.id, d.data())));
  });
}
